import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Video, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

/**
 * Zoom health + backfill panel for the Admin → Schedules page.
 * Shows configured status, licensed-host verification, upcoming-class coverage,
 * and provides a "Backfill" button to auto-create Zoom meetings for any
 * upcoming class that doesn't have one.
 */
const ZoomHealthPanel = ({ onBackfilled }) => {
  const [status, setStatus] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const [statusRes, conflictsRes] = await Promise.all([
        api.get('/admin/zoom/status'),
        api.get('/admin/zoom/conflicts?days=7').catch(() => ({ data: { conflicts: [] } })),
      ]);
      setStatus(statusRes.data);
      setConflicts(conflictsRes.data?.conflicts || []);
    } catch (e) {
      toast.error('Failed to load Zoom status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleBackfill = async () => {
    try {
      setBackfilling(true);
      const res = await api.post('/admin/zoom/backfill', { limit: 500 });
      const { checked, created, conflicts, errors = [], error_count } = res.data;
      const errCount = error_count ?? errors.length;
      if (created > 0) toast.success(`Provisioned ${created} Zoom meeting${created > 1 ? 's' : ''}`);
      if (conflicts > 0) toast.warning(`${conflicts} class${conflicts > 1 ? 'es' : ''} could not be scheduled — both licensed hosts busy`);
      if (errCount > 0) toast.error(`${errCount} error${errCount > 1 ? 's' : ''} — see console`);
      if (created === 0 && conflicts === 0 && errCount === 0) toast.info(checked === 0 ? 'No classes to backfill' : 'Nothing to do');
      if (errCount > 0) console.warn('[zoom backfill] errors:', errors);
      await refresh();
      if (onBackfilled) onBackfilled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-5 mb-8 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Zoom status…
      </div>
    );
  }

  if (!status) return null;

  const anyUnverified = (status.hosts || []).some(h => !h.verified);
  const allVerified = (status.hosts || []).length > 0 && !anyUnverified;
  const missing = status?.upcoming?.missing ?? 0;
  const conflictCount = status?.upcoming?.conflicts ?? 0;

  return (
    <div
      className="glass-card rounded-xl border-2 border-blue-100 p-5 mb-8"
      data-testid="zoom-health-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
            <Video className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Zoom Integration</h3>
            <p className="text-xs text-slate-500 font-semibold">
              {status.configured ? 'Server-to-Server OAuth active' : 'Not configured'} · {status.host_pool?.length || 0} licensed host{(status.host_pool?.length || 0) === 1 ? '' : 's'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={loading}
            data-testid="zoom-refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleBackfill}
            disabled={backfilling || !status.configured || missing === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="zoom-backfill-btn"
          >
            {backfilling ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Backfilling…</>
            ) : (
              <><Video className="w-3.5 h-3.5 mr-1.5" /> Backfill {missing > 0 ? `(${missing})` : ''}</>
            )}
          </Button>
        </div>
      </div>

      {/* Host verification list */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
        {(status.hosts || []).map((h, i) => (
          <div
            key={h.email}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${h.verified ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
            data-testid={`zoom-host-${i}`}
          >
            {h.verified ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-800 truncate">{h.email}</div>
              {h.verified ? (
                <div className="text-xs text-emerald-700">
                  {h.type === 2 ? 'Licensed' : h.type === 1 ? 'Basic (unlicensed)' : 'Verified'}
                  {h.display_name ? ` · ${h.display_name}` : ''}
                </div>
              ) : (
                <div className="text-xs text-amber-700 truncate" title={h.error}>{h.error}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Upcoming class coverage */}
      <div className="mt-4 grid grid-cols-4 gap-3 text-center">
        <div className="rounded-lg bg-slate-50 py-2">
          <div className="text-xl font-bold text-slate-800">{status.upcoming?.total ?? 0}</div>
          <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wide">Upcoming</div>
        </div>
        <div className="rounded-lg bg-emerald-50 py-2">
          <div className="text-xl font-bold text-emerald-700">{status.upcoming?.with_meeting ?? 0}</div>
          <div className="text-[11px] text-emerald-600 font-semibold uppercase tracking-wide">Provisioned</div>
        </div>
        <div className="rounded-lg bg-amber-50 py-2">
          <div className="text-xl font-bold text-amber-700">{missing}</div>
          <div className="text-[11px] text-amber-600 font-semibold uppercase tracking-wide">Missing</div>
        </div>
        <div className="rounded-lg bg-red-50 py-2">
          <div className="text-xl font-bold text-red-700">{conflictCount}</div>
          <div className="text-[11px] text-red-600 font-semibold uppercase tracking-wide">Conflicts</div>
        </div>
      </div>

      {!allVerified && status.configured && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 flex gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            One or more host emails aren&apos;t verified. Fixes: (1) add the <code>user:read:user</code> scope to your S2S OAuth app in Zoom Marketplace, (2) confirm the exact email is inside your Zoom workspace.
          </span>
        </div>
      )}

      {/* Weekly Conflicts */}
      {conflicts.length > 0 && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="zoom-conflicts-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <div className="text-sm font-bold text-red-800">
              {conflicts.length} slot{conflicts.length > 1 ? 's' : ''} this week couldn&apos;t get a Zoom meeting
            </div>
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {conflicts.map(c => (
              <div key={c.id} className="text-xs bg-white rounded px-2 py-1.5 flex items-center justify-between gap-2" data-testid={`conflict-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-slate-800">{c.student_name}</span>
                  <span className="text-slate-500"> · {c.teacher_name}</span>
                </div>
                <div className="text-slate-600 font-mono text-[11px] flex-shrink-0">
                  {c.date} · {c.time} {c.timezone}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-red-700">
            Both licensed hosts are already booked in these slots. Move the class time or add a third license.
          </div>
        </div>
      )}

      {/* Webhook Registration Helper */}
      {status.webhook && (
        <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="font-bold text-slate-700 mb-1 flex items-center gap-2">
            <Video className="w-3.5 h-3.5" />
            Webhook Endpoint {status.webhook.secret_configured ? (
              <span className="text-emerald-600 text-[10px] uppercase tracking-wide">secret set</span>
            ) : (
              <span className="text-red-600 text-[10px] uppercase tracking-wide">secret missing</span>
            )}
          </div>
          <div className="text-slate-500 mb-2">
            Paste this URL into your S2S OAuth app → Feature → Event Subscriptions. Subscribe to <code>meeting.started</code>, <code>meeting.ended</code>, <code>meeting.participant_joined</code>, <code>meeting.participant_left</code>.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-slate-200 rounded px-2 py-1 text-slate-800 text-[11px] break-all" data-testid="zoom-webhook-url">
              {(typeof window !== 'undefined' ? window.location.origin : '') + status.webhook.endpoint_path}
            </code>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={() => {
                const url = (typeof window !== 'undefined' ? window.location.origin : '') + status.webhook.endpoint_path;
                navigator.clipboard?.writeText(url);
                toast.success('Webhook URL copied');
              }}
              data-testid="zoom-webhook-copy"
            >Copy</Button>
          </div>
          {!status.webhook.secret_configured && (
            <div className="mt-2 text-amber-700">
              After creating the Event Subscription, copy the &quot;Secret Token&quot; into <code>ZOOM_WEBHOOK_SECRET_TOKEN</code> in backend/.env and restart the backend.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ZoomHealthPanel;
